import React, { useState, useEffect } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../hooks/useNotification';
import { Modal } from '../design-system';
import apiClient from '../services/unifiedApiClient';
import GitHubIntegration from '../components/GitHubIntegration';
import { useSubscription } from '../contexts/SubscriptionContext';

const T = {
  paper:      '#F7EFD8',
  paperDeep:  '#EEE2BF',
  paperSoft:  '#FDF7E5',
  card:       '#FFFBEC',
  ink:        '#2E2109',
  ink2:       '#4A3510',
  inkMuted:   '#6E5A38',
  inkFaint:   '#A39068',
  rule:       '#D7C9A1',
  ruleStrong: '#BFAB79',
  orange:     '#EA7C1F',
  orangeDark: '#C25F0E',
  moss:       '#6B7A3E',
  mossDark:   '#4E5A29',
  mossSoft:   '#D7DCB9',
  capRed:     '#C14A33',
  capRedSoft: '#F2C7BA',
  roof:       '#C76A3B',
  roofSoft:   '#F2D1B3',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// Squiggle underline SVG for section titles
const SquiggleLine = () => (
  <svg width="60" height="6" viewBox="0 0 60 6" fill="none" style={{ display: 'block', marginTop: 4 }}>
    <path d="M0 3 Q7.5 0 15 3 Q22.5 6 30 3 Q37.5 0 45 3 Q52.5 6 60 3" stroke={T.orange} strokeWidth="1.5" fill="none" strokeLinecap="round"/>
  </svg>
);

const SectionTitle = ({ children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontSize: 20, fontWeight: 800, color: T.ink, fontFamily: T.sans }}>{children}</div>
    <SquiggleLine />
  </div>
);

const CottageCard = ({ children, style = {} }) => (
  <div style={{
    background: T.card,
    border: `1.5px solid ${T.rule}`,
    borderRadius: T.r4,
    boxShadow: T.sh2,
    padding: 28,
    marginBottom: 20,
    ...style,
  }}>
    {children}
  </div>
);

const CottageInput = ({ label, value, onChange, placeholder, readOnly, type = 'text' }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 700, color: T.inkMuted, fontFamily: T.sans }}>{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{
          padding: '9px 13px',
          border: `1.5px solid ${focused ? T.ruleStrong : T.rule}`,
          borderRadius: T.r3,
          background: readOnly ? T.paperDeep : T.paperSoft,
          color: readOnly ? T.inkFaint : T.ink,
          fontSize: 14,
          fontFamily: T.sans,
          fontWeight: 600,
          outline: 'none',
          boxShadow: focused ? `0 0 0 3px ${T.orange}22` : 'none',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          cursor: readOnly ? 'default' : 'text',
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    </div>
  );
};

const CottageButton = ({ children, onClick, variant = 'soft', size = 'md', disabled, type = 'button' }) => {
  const [hovered, setHovered] = useState(false);

  const base = {
    fontFamily: T.sans,
    fontWeight: 800,
    border: 'none',
    borderRadius: T.full,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.15s, box-shadow 0.15s, transform 0.1s',
    opacity: disabled ? 0.55 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    lineHeight: 1,
  };

  const sizes = {
    sm: { fontSize: 12, padding: '7px 14px' },
    md: { fontSize: 14, padding: '10px 20px' },
    lg: { fontSize: 15, padding: '12px 26px' },
  };

  const variants = {
    primary: {
      background: hovered ? T.orangeDark : T.orange,
      color: '#fff',
      boxShadow: hovered ? T.sh2 : T.sh1,
    },
    soft: {
      background: hovered ? T.paperDeep : T.paper,
      color: T.ink2,
      border: `1.5px solid ${T.rule}`,
      boxShadow: hovered ? T.sh1 : 'none',
    },
    ghost: {
      background: 'transparent',
      color: T.inkMuted,
      border: `1.5px dashed ${T.rule}`,
    },
    danger: {
      background: hovered ? '#a83a25' : T.capRed,
      color: '#fff',
      boxShadow: hovered ? T.sh2 : T.sh1,
    },
    moss: {
      background: hovered ? T.mossDark : T.moss,
      color: '#fff',
      boxShadow: hovered ? T.sh2 : T.sh1,
    },
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...base, ...sizes[size], ...variants[variant] }}
    >
      {children}
    </button>
  );
};

// Toggle component
const Toggle = ({ checked, onChange }) => {
  return (
    <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, cursor: 'pointer', flexShrink: 0 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ display: 'none' }} />
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        background: checked ? T.moss : T.rule,
        borderRadius: 12,
        transition: 'background 0.2s',
      }}>
        <div style={{
          position: 'absolute',
          width: 18, height: 18,
          top: 3, left: checked ? 23 : 3,
          background: '#fff',
          borderRadius: '50%',
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }} />
      </div>
    </label>
  );
};

// Jira/Trello credential fields
const CredentialInput = ({ label, type = 'text', value, onChange, placeholder, helpText }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 700, color: T.inkMuted, fontFamily: T.sans }}>{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: '9px 13px',
          border: `1.5px solid ${focused ? T.ruleStrong : T.rule}`,
          borderRadius: T.r3,
          background: T.paperSoft,
          color: T.ink,
          fontSize: 14,
          fontFamily: T.sans,
          fontWeight: 600,
          outline: 'none',
          boxShadow: focused ? `0 0 0 3px ${T.orange}22` : 'none',
          transition: 'border-color 0.15s, box-shadow 0.15s',
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {helpText && (
        <div style={{ fontSize: 12, color: T.inkFaint, fontFamily: T.sans }}>{helpText}</div>
      )}
    </div>
  );
};

// Expandable integration card for Trello / Jira
const ExpandableIntegration = ({ name, enabled, onToggle, children }) => {
  const initial = name.charAt(0).toUpperCase();
  return (
    <div style={{
      border: `1.5px solid ${enabled ? T.ruleStrong : T.rule}`,
      borderRadius: T.r3,
      background: T.paperSoft,
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
        <div style={{
          width: 34, height: 34, borderRadius: T.r3,
          background: enabled ? T.moss : T.ink,
          color: '#fff', fontWeight: 800, fontSize: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: T.sans, flexShrink: 0,
        }}>
          {initial}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: T.ink, fontFamily: T.sans }}>{name}</div>
          <div style={{ fontSize: 12, color: enabled ? T.moss : T.inkMuted, fontFamily: T.sans, marginTop: 1 }}>
            {enabled ? 'Connected' : 'Not connected'}
          </div>
        </div>
        <Toggle checked={enabled} onChange={onToggle} />
      </div>
      {enabled && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px dashed ${T.rule}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {children}
        </div>
      )}
    </div>
  );
};

const Settings = () => {
  const { settings, updateSettings, settingsLoaded } = useSettings();
  const { logout, user } = useAuth();
  const { subscription, openBillingPortal, isPro, isHobbyist, isEnterprise, isFree, loading: billingLoading } = useSubscription();
  const { setGlobalLoading } = useApp();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const defaultSettings = {
    themeMode: 'system',
    notifications: true,
    trelloIntegration: { enabled: false, apiKey: '', token: '' },
    jiraIntegration: { enabled: false, jiraUrl: '', userEmail: '', apiToken: '' },
  };
  const [localSettings, setLocalSettings] = useState(settings || defaultSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [jiraTestStatus, setJiraTestStatus] = useState(null);

  useEffect(() => {
    if (settingsLoaded) {
      setLocalSettings(settings);
    }
  }, [settings, settingsLoaded]);

  const handleChange = (section, field, value) => {
    if (section === 'trelloIntegration' || section === 'jiraIntegration') {
      setLocalSettings((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value },
      }));
    } else {
      setLocalSettings((prev) => ({ ...prev, [field]: value }));
      if (field === 'themeMode') {
        const labels = { light: 'Light', dark: 'Dark', system: 'System Default' };
        showNotification(`Theme set to ${labels[value]}`, 'success');
        updateSettings({ ...localSettings, [field]: value });
      } else if (field === 'notifications') {
        showNotification(value ? 'Email notifications enabled' : 'Email notifications disabled', 'success');
        updateSettings({ ...localSettings, [field]: value });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (localSettings.trelloIntegration?.enabled &&
          localSettings.trelloIntegration?.apiKey &&
          localSettings.trelloIntegration?.token) {
        await apiClient.post('/trello/credentials', {
          api_key: localSettings.trelloIntegration?.apiKey,
          token: localSettings.trelloIntegration?.token,
        });
      }
      if (localSettings.jiraIntegration?.enabled &&
          localSettings.jiraIntegration?.jiraUrl &&
          localSettings.jiraIntegration?.userEmail &&
          localSettings.jiraIntegration?.apiToken) {
        await apiClient.post('/jira/credentials', {
          jira_url: localSettings.jiraIntegration?.jiraUrl,
          user_email: localSettings.jiraIntegration?.userEmail,
          api_token: localSettings.jiraIntegration?.apiToken,
        });
      }
      updateSettings(localSettings);
      showNotification('Settings saved!', 'success');
    } catch (error) {
      showNotification('Failed to save settings. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    setGlobalLoading(true);
    try {
      await logout();
      navigate('/');
    } catch (error) {
      showNotification('Logout failed. Please try again.', 'error');
    } finally {
      setGlobalLoading(false);
      setShowLogoutModal(false);
    }
  };

  const testJiraConnection = async () => {
    const { jiraUrl, userEmail, apiToken } = localSettings.jiraIntegration || {};
    if (!jiraUrl || !userEmail || !apiToken) return;
    setJiraTestStatus('loading');
    try {
      const result = await apiClient.post('/jira/test-connection', {
        jira_url: jiraUrl, email: userEmail, api_token: apiToken,
      });
      setJiraTestStatus({ ok: true, message: `Connected to ${result.server_title} as ${result.display_name}` });
    } catch (err) {
      const detail = err.response?.data?.detail || err.userMessage || err.message || 'Connection failed';
      setJiraTestStatus({ ok: false, message: detail });
    }
  };

  const githubConnected = !!user?.github_username;
  const trelloEnabled = localSettings.trelloIntegration?.enabled;
  const jiraEnabled = localSettings.jiraIntegration?.enabled;

  return (
    <div style={{
      padding: '28px 32px 40px',
      maxWidth: 820,
      margin: '0 auto',
      fontFamily: T.sans,
    }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: T.hand, fontSize: 26, color: T.orange, lineHeight: 1 }}>settings —</div>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: T.ink, margin: '6px 0 22px', fontFamily: T.sans, lineHeight: 1.1 }}>
          Make it yours.
        </h1>
      </div>

      <form onSubmit={handleSubmit}>

        {/* Card 1: General */}
        <CottageCard>
          <SectionTitle>General</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <CottageInput
              label="Email"
              value={user?.email || ''}
              readOnly
              type="email"
              placeholder="your@email.com"
            />
            {githubConnected && (
              <CottageInput
                label="Connected repo"
                value={user?.github_username ? `github.com/${user.github_username}` : ''}
                readOnly
                placeholder="No repo connected"
              />
            )}
          </div>
        </CottageCard>

        {/* Card 2: Integrations */}
        <CottageCard>
          <SectionTitle>Integrations</SectionTitle>
          {/* GitHub detail component */}
          <GitHubIntegration />

          {/* Trello expandable */}
          <div style={{ marginTop: 12 }}>
            <ExpandableIntegration
              name="Trello"
              enabled={trelloEnabled}
              onToggle={(v) => handleChange('trelloIntegration', 'enabled', v)}
            >
              <CredentialInput
                label="Trello API Key"
                value={localSettings.trelloIntegration?.apiKey}
                onChange={(v) => handleChange('trelloIntegration', 'apiKey', v)}
                placeholder="Enter your Trello API Key"
                helpText={
                  <a href="https://trello.com/app-key" target="_blank" rel="noopener noreferrer"
                    style={{ color: T.orange, textDecoration: 'none', fontFamily: T.sans }}>
                    Get your API key from Trello →
                  </a>
                }
              />
              <CredentialInput
                label="Trello Token"
                type="password"
                value={localSettings.trelloIntegration?.token}
                onChange={(v) => handleChange('trelloIntegration', 'token', v)}
                placeholder="Enter your Trello Token"
                helpText="Generate a token from the same API key page"
              />
            </ExpandableIntegration>
          </div>

          {/* Jira expandable */}
          <div style={{ marginTop: 12 }}>
            <ExpandableIntegration
              name="Jira"
              enabled={jiraEnabled}
              onToggle={(v) => {
                handleChange('jiraIntegration', 'enabled', v);
                setJiraTestStatus(null);
              }}
            >
              <CredentialInput
                label="Jira URL"
                type="url"
                value={localSettings.jiraIntegration?.jiraUrl || ''}
                onChange={(v) => handleChange('jiraIntegration', 'jiraUrl', v)}
                placeholder="https://your-domain.atlassian.net"
                helpText="Your Atlassian Cloud instance URL"
              />
              <CredentialInput
                label="Email Address"
                type="email"
                value={localSettings.jiraIntegration?.userEmail || ''}
                onChange={(v) => handleChange('jiraIntegration', 'userEmail', v)}
                placeholder="your.email@example.com"
                helpText="Your Jira account email address"
              />
              <CredentialInput
                label="API Token"
                type="password"
                value={localSettings.jiraIntegration?.apiToken || ''}
                onChange={(v) => {
                  handleChange('jiraIntegration', 'apiToken', v);
                  setJiraTestStatus(null);
                }}
                placeholder="Enter your Jira API token"
                helpText={
                  <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer"
                    style={{ color: T.orange, textDecoration: 'none', fontFamily: T.sans }}>
                    Generate an API token from Atlassian →
                  </a>
                }
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                <CottageButton
                  type="button"
                  variant="soft"
                  size="sm"
                  onClick={testJiraConnection}
                  disabled={
                    jiraTestStatus === 'loading' ||
                    !localSettings.jiraIntegration?.jiraUrl ||
                    !localSettings.jiraIntegration?.userEmail ||
                    !localSettings.jiraIntegration?.apiToken
                  }
                >
                  {jiraTestStatus === 'loading' ? 'Testing…' : 'Test Connection'}
                </CottageButton>
                {jiraTestStatus && jiraTestStatus !== 'loading' && (
                  <span style={{
                    fontSize: 12,
                    fontFamily: T.sans,
                    fontWeight: 700,
                    color: jiraTestStatus.ok ? T.moss : T.capRed,
                  }}>
                    {jiraTestStatus.ok ? '✓ ' : '✗ '}{jiraTestStatus.message}
                  </span>
                )}
              </div>
            </ExpandableIntegration>
          </div>
        </CottageCard>

        {/* Card 3: Billing / Subscription */}
        <CottageCard>
          <SectionTitle>Subscription</SectionTitle>
          <div style={{
            padding: '14px 16px',
            background: T.paperDeep,
            border: `1.5px solid ${T.rule}`,
            borderRadius: T.r3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 12,
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: T.ink, fontFamily: T.sans }}>
                  {isEnterprise ? 'Enterprise' : isPro ? 'Pro' : isHobbyist ? 'Hobbyist' : 'Free'}
                </span>
                {(isPro || isEnterprise || isHobbyist) && (
                  <span style={{
                    background: T.moss,
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    padding: '2px 8px',
                    borderRadius: T.full,
                    fontFamily: T.sans,
                  }}>Active</span>
                )}
              </div>
              <div style={{ fontSize: 13, color: T.inkMuted, fontFamily: T.sans }}>
                {isEnterprise
                  ? 'Full access to all features including SSO and priority support'
                  : isPro
                  ? 'GitHub integration, exports, and priority support'
                  : isHobbyist
                  ? 'Exports, AI chat, and AGENTS.md bundle'
                  : 'Event modeling, features and user stories'}
              </div>
            </div>
            {isFree && (
              <CottageButton variant="primary" size="sm" onClick={() => navigate('/pricing')}>
                Upgrade
              </CottageButton>
            )}
          </div>

          {(isPro || isEnterprise || isHobbyist) && subscription?.stripe_customer_id && (
            <div style={{
              padding: '14px 16px',
              background: T.paperSoft,
              border: `1.5px dashed ${T.rule}`,
              borderRadius: T.r3,
            }}>
              <div style={{ fontSize: 13, color: T.inkMuted, fontFamily: T.sans, marginBottom: 10 }}>
                Update payment methods, view invoices, or cancel your subscription.
              </div>
              <CottageButton variant="soft" size="sm" onClick={() => openBillingPortal()} disabled={billingLoading}>
                {billingLoading ? 'Loading…' : 'Manage Billing'}
              </CottageButton>
            </div>
          )}

          {isFree && (
            <div style={{
              padding: '14px 16px',
              background: `${T.orange}0f`,
              border: `1.5px solid ${T.orange}44`,
              borderRadius: T.r3,
              marginTop: 12,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.ink2, fontFamily: T.sans, marginBottom: 8 }}>
                Upgrade to Pro for more
              </div>
              <ul style={{ margin: '0 0 12px 0', padding: '0 0 0 16px', color: T.ink2, fontSize: 13, fontFamily: T.sans }}>
                <li style={{ marginBottom: 4 }}>GitHub Integration</li>
                <li>Export to JSON, Markdown, YAML</li>
              </ul>
              <CottageButton variant="primary" size="sm" onClick={() => navigate('/pricing')}>
                View Pricing
              </CottageButton>
            </div>
          )}
        </CottageCard>

        {/* Card 4: Account */}
        <CottageCard>
          <SectionTitle>Account</SectionTitle>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.ink, fontFamily: T.sans }}>Sign out</div>
              <div style={{ fontSize: 12, color: T.inkMuted, fontFamily: T.sans, marginTop: 2 }}>
                Sign out of your account.
              </div>
            </div>
            <CottageButton variant="ghost" size="sm" onClick={() => setShowLogoutModal(true)}>
              Sign out
            </CottageButton>
          </div>
        </CottageCard>

        {/* Save button */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingTop: 8,
        }}>
          <CottageButton type="submit" variant="primary" size="md" disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save settings'}
          </CottageButton>
        </div>
      </form>

      {/* Logout Confirmation Modal */}
      <Modal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title="Sign out?"
      >
        <div style={{ fontFamily: T.sans, fontSize: 14, color: T.ink2, marginBottom: 24, lineHeight: 1.6 }}>
          Are you sure you want to sign out? You'll need to sign in again to access your workspace.
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <CottageButton variant="soft" size="sm" onClick={() => setShowLogoutModal(false)}>
            Cancel
          </CottageButton>
          <CottageButton variant="danger" size="sm" onClick={handleLogout}>
            Sign out
          </CottageButton>
        </div>
      </Modal>
    </div>
  );
};

export default Settings;
